import { Check } from "lucide-react";
import { PRICING } from "@/config/site";
import { Reveal } from "@/components/motion";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { scrollToId } from "@/lib/scroll";

const monthlyFeatures = [
  "Full CoachER access, month to month",
  "AI-assisted practice planning",
  "Lineups & batting orders",
  "Rosters, notes & player development",
  "Assistant Coaches included at no extra cost",
  "Multiple teams supported",
];

const annualFeatures = [
  "Everything in Monthly",
  "Only $4.92/month — billed $59/year",
  "Save ~30% vs. paying monthly",
  "Season-to-season digital dugout",
  "Assistant Coaches included at no extra cost",
  "Multiple teams supported",
];

const FeatureList = ({ items, dark }) => (
  <ul className="mt-8 space-y-3.5">
    {items.map((f) => (
      <li key={f} className={`flex items-start gap-3 text-sm leading-relaxed ${dark ? "text-white/85" : "text-coacher-slate"}`}>
        <Check size={17} className={`mt-0.5 shrink-0 ${dark ? "text-coacher-red" : "text-coacher-navy"}`} strokeWidth={3} />
        {f}
      </li>
    ))}
  </ul>
);

const PricingCards = () => (
  <div data-testid="pricing-cards" className="grid gap-6 lg:grid-cols-2 lg:gap-8 max-w-4xl mx-auto">
    <Reveal>
      <div data-testid="pricing-monthly-card" className="flex h-full flex-col rounded-xl border border-coacher-border bg-white p-8 shadow-card">
        <p className="font-display text-lg font-semibold uppercase tracking-widest text-coacher-slate">Monthly</p>
        <div className="mt-5 flex items-end gap-2">
          <span className="font-display text-6xl font-semibold text-coacher-navy">{PRICING.monthly.display}</span>
          <span className="pb-2 text-sm font-medium text-coacher-slate">{PRICING.monthly.period}</span>
        </div>
        <p className="mt-3 text-sm text-coacher-slate">Flexible month-to-month CoachER access.</p>
        <FeatureList items={monthlyFeatures} dark={false} />
        <button
          data-testid="pricing-monthly-cta"
          onClick={() => {
            trackEvent(EVENTS.pricingMonthlySelected);
            trackEvent(EVENTS.pricingTrialCtaClicked, { plan: "monthly" });
            scrollToId("launch-notify");
          }}
          className="mt-9 inline-flex h-12 items-center justify-center rounded-md border-2 border-coacher-navy text-sm font-semibold text-coacher-navy transition-colors duration-200 hover:bg-coacher-navy hover:text-white"
        >
          Start Free Trial
        </button>
      </div>
    </Reveal>

    <Reveal delay={0.12}>
      <div data-testid="pricing-annual-card" className="relative flex h-full flex-col rounded-xl border-2 border-coacher-navy bg-coacher-navy p-8 shadow-frame">
        <span data-testid="pricing-annual-badge" className="absolute -top-3.5 left-8 rounded-sm bg-coacher-red px-3 py-1 text-xs font-bold uppercase tracking-widest text-white">
          {PRICING.annual.badge}
        </span>
        <p className="font-display text-lg font-semibold uppercase tracking-widest text-white/70">Annual</p>
        <div className="mt-5 flex items-end gap-2">
          <span className="font-display text-6xl font-semibold text-white">{PRICING.annual.display}</span>
          <span className="pb-2 text-sm font-medium text-white/70">{PRICING.annual.period}</span>
        </div>
        <p className="mt-3 text-sm text-white/80">
          Only {PRICING.annual.monthlyEquivalent}/month · Save ~{PRICING.annual.savingsPercent}% vs. monthly
        </p>
        <FeatureList items={annualFeatures} dark />
        <button
          data-testid="pricing-annual-cta"
          onClick={() => {
            trackEvent(EVENTS.pricingAnnualSelected);
            trackEvent(EVENTS.pricingTrialCtaClicked, { plan: "annual" });
            scrollToId("launch-notify");
          }}
          className="mt-9 inline-flex h-12 items-center justify-center rounded-md bg-coacher-red text-sm font-semibold text-white transition-colors duration-200 hover:bg-coacher-red-dark"
        >
          Start Free Trial
        </button>
      </div>
    </Reveal>
  </div>
);

export default PricingCards;
